/**
 * Data layer for `ccpa-dsr`. Thin typed fetch wrappers over /api/v1/ccpa-dsr.
 *
 * Backend shape (see apps/api/src/routes/ccpa-dsr.ts):
 *   - POST /requests          create subject request OR verify token
 *   - GET  /requests[?status] tenant-scoped list with derived SLA fields
 *   - PATCH /requests/:id/status  transition to fulfilled | denied
 *   - GET  /do-not-sell       public machine-readable GPC payload
 *
 * Raw fetch() is confined to this file per the dash D6 contract.
 */

export type CcpaRequestType =
	| "know"
	| "delete"
	| "opt_out_sale"
	| "limit_sensitive";

export type CcpaRequestStatus =
	| "received"
	| "verified"
	| "fulfilled"
	| "denied";

export interface CcpaRequestRow {
	id: string;
	tenant_id: string;
	subject_email: string;
	ca_resident_attested: number;
	type: CcpaRequestType;
	status: CcpaRequestStatus;
	received_at: number;
	verified_at: number | null;
	fulfilled_at: number | null;
	denial_reason: string | null;
	days_since_received: number;
	sla_breached: boolean;
}

export interface CreateCcpaRequestBody {
	subject_email: string;
	ca_resident_attested: boolean;
	type: CcpaRequestType;
}

export interface CreateCcpaRequestResponse {
	id: string;
	tenant_id: string;
	subject_email: string;
	type: CcpaRequestType;
	status: CcpaRequestStatus;
	received_at: number;
	verification_token: string;
}

export interface PatchCcpaStatusBody {
	status: "fulfilled" | "denied";
	denial_reason?: string;
}

const BASE = "/api/v1/ccpa-dsr";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	statusFilter?: CcpaRequestStatus,
): Promise<CcpaRequestRow[]> {
	const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
	const res = await fetch(`${BASE}/requests${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CcpaRequestRow[]>(res);
}

export async function create(
	body: CreateCcpaRequestBody,
): Promise<CreateCcpaRequestResponse> {
	const res = await fetch(`${BASE}/requests`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCcpaRequestResponse>(res);
}

export async function patchStatus(
	id: string,
	body: PatchCcpaStatusBody,
): Promise<CcpaRequestRow> {
	const res = await fetch(
		`${BASE}/requests/${encodeURIComponent(id)}/status`,
		{
			method: "PATCH",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<CcpaRequestRow>(res);
}

export const CCPA_REQUEST_TYPES: readonly CcpaRequestType[] = [
	"know",
	"delete",
	"opt_out_sale",
	"limit_sensitive",
] as const;

export const CCPA_REQUEST_STATUSES: readonly CcpaRequestStatus[] = [
	"received",
	"verified",
	"fulfilled",
	"denied",
] as const;

export const CCPA_TYPE_LABELS: Record<CcpaRequestType, string> = {
	know: "Right to know",
	delete: "Right to delete",
	opt_out_sale: "Opt out of sale",
	limit_sensitive: "Limit sensitive use",
};

export const ccpaDsrQueryKeys = {
	all: ["ccpa-dsr"] as const,
	list: (statusFilter?: CcpaRequestStatus) =>
		statusFilter
			? (["ccpa-dsr", "list", statusFilter] as const)
			: (["ccpa-dsr", "list"] as const),
	detail: (id: string) => ["ccpa-dsr", id] as const,
};
